import type {
  BlockSelection,
  MapartPalette,
  PaletteBlock,
  PaletteEntry,
  Rgb,
  StaircaseMode,
  ToneKey,
} from './types';

const PALETTE_URL = '/data/mapart-palette-1.8.json';

let cached: MapartPalette | null = null;
let inFlight: Promise<MapartPalette> | null = null;

export async function loadPalette(): Promise<MapartPalette> {
  if (cached) return cached;
  if (!inFlight) {
    inFlight = fetch(PALETTE_URL)
      .then((r) => r.json() as Promise<MapartPalette>)
      .then((p) => {
        cached = p;
        return p;
      });
  }
  return inFlight;
}

export function colourIds(palette: MapartPalette): number[] {
  return Object.keys(palette.colors)
    .map(Number)
    .filter((id) => id > 0)
    .sort((a, b) => a - b);
}

export function tonesFor(mode: StaircaseMode): ToneKey[] {
  return mode === 'off' ? ['normal'] : ['dark', 'normal', 'light'];
}

export type PalettePreset =
  | 'recommended'
  | 'none'
  | 'wool'
  | 'carpet'
  | 'stainedClay'
  | 'stainedGlass'
  | 'fullCubes'
  | 'noGravity';

export const PALETTE_PRESETS: { key: PalettePreset; localeKey: string }[] = [
  { key: 'recommended', localeKey: 'MAPART_PALETTE_RECOMMENDED' },
  { key: 'fullCubes', localeKey: 'MAPART_PALETTE_FULL_CUBES' },
  { key: 'noGravity', localeKey: 'MAPART_PALETTE_NO_GRAVITY' },
  { key: 'wool', localeKey: 'MAPART_PALETTE_WOOL' },
  { key: 'carpet', localeKey: 'MAPART_PALETTE_CARPET' },
  { key: 'stainedClay', localeKey: 'MAPART_PALETTE_STAINED_CLAY' },
  { key: 'stainedGlass', localeKey: 'MAPART_PALETTE_STAINED_GLASS' },
  { key: 'none', localeKey: 'MAPART_PALETTE_NONE' },
];

const FAMILY_BLOCK_ID: Partial<Record<PalettePreset, number>> = {
  wool: 35,
  carpet: 171,
  stainedClay: 159,
  stainedGlass: 95,
};

/** Liquids and creative-only blocks are never picked automatically: water shades come from depth. */
const isBuildable = (block: PaletteBlock) =>
  !block.flags.includes('creativeOnly') && !block.flags.includes('liquid');

function acceptorFor(preset: PalettePreset): (block: PaletteBlock) => boolean {
  const familyId = FAMILY_BLOCK_ID[preset];
  if (familyId !== undefined) return (block) => block.id === familyId;
  if (preset === 'fullCubes') return (block) => !block.flags.includes('notFullCube');
  if (preset === 'noGravity')
    return (block) => !block.flags.includes('gravity') && !block.flags.includes('needsSupport');
  return () => true;
}

export function selectionForPreset(palette: MapartPalette, preset: PalettePreset): BlockSelection {
  if (preset === 'none') return {};
  const accept = acceptorFor(preset);
  const selection: BlockSelection = {};
  for (const id of colourIds(palette)) {
    const blocks = palette.colors[id].blocks;
    const preferred = blocks.findIndex((b) => b.recommended && isBuildable(b) && accept(b));
    const usable = blocks.findIndex((b) => isBuildable(b) && accept(b));
    selection[id] = preferred >= 0 ? preferred : usable >= 0 ? usable : null;
  }
  return selection;
}

export function defaultSelection(palette: MapartPalette): BlockSelection {
  return selectionForPreset(palette, 'recommended');
}

export function rgbToLab([r, g, b]: Rgb): Rgb {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const rl = lin(r);
  const gl = lin(g);
  const bl = lin(b);
  const x = (0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl) / 0.95047;
  const y = 0.2126729 * rl + 0.7151522 * gl + 0.072175 * bl;
  const z = (0.0193339 * rl + 0.119192 * gl + 0.9503041 * bl) / 1.08883;
  const f = (t: number) => (t > 0.008856452 ? Math.cbrt(t) : (903.2963 * t + 16) / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

export function buildPaletteEntries(
  palette: MapartPalette,
  selection: BlockSelection,
  mode: StaircaseMode,
): PaletteEntry[] {
  const tones = tonesFor(mode);
  const entries: PaletteEntry[] = [];
  for (const colourId of colourIds(palette)) {
    const blockIndex = selection[colourId];
    if (blockIndex === null || blockIndex === undefined) continue;
    const colour = palette.colors[colourId];
    const block = colour.blocks[blockIndex];
    if (!block) continue;
    for (const tone of tones) {
      const rgb = colour.tonesRgb[tone];
      entries.push({ colourId, tone, rgb, lab: rgbToLab(rgb), block });
    }
  }
  return entries;
}

